/* =========================================================================
   Routinae — Routine Finder: deterministic engine.

   PURE LOGIC. No DOM, no copy, no network. Given the answer matrix and a
   catalogue index, it returns a profile, a routine plan, a list of things it
   deliberately did NOT recommend, and a list of advisory rules.

   Why deterministic:
     Final_research.md Decision 4 — "a deterministic rules engine above any
     generative model". The engine decides the PRODUCTS. The optional AI layer
     (see /ai) may rewrite the PROSE, and may propose ANSWERS the visitor could
     have given themselves — but every product decision still comes from the
     rules below, applied to an answer matrix. That boundary is what keeps the
     tool auditable under Reg. (EC) 1223/2009 Art. 20 + Reg. (EU) 655/2013.

   Vocabulary discipline (regulatory, not stylistic):
     - We never emit a condition name as something we address. No "eczema",
       "dermatitis", "acne", "rosacea", "keratosis pilaris", "dandruff".
     - Outputs describe APPEARANCE and PRODUCT CHOICE, never treatment.
     - Any answer that points at a condition emits an ESCALATION rule instead
       of a product.

   Everything user-visible is a KEY. The theme resolves keys to translated
   strings from section/block settings. Nothing here is ever shown raw.
   ========================================================================= */

(function (root) {
  'use strict';

  /* ---------------------------------------------------------------------
     1. Question graph.

     Logic only — the wording lives in section blocks and is looked up by
     `key` + option INDEX. Option order is therefore load-bearing: the theme
     may re-word an option but must never reorder one.

     ADAPTIVE FOLLOW-UPS, August 2026. The set used to be fixed: every
     visitor answered the same nine questions in the same order, which made
     the tool feel like a form rather than a conversation, and it threw away
     resolution exactly where the answers were most informative. Four of the
     seven concerns now open one follow-up each, and an exfoliating shelf
     opens a frequency question. Two visitors with different answers now get
     genuinely different questionnaires — nine questions on the shortest
     path, eleven on the longest — and every follow-up obeys the same rule
     as every original question (research/routine-finder-2026.md §5.1): an
     answer that changes no output does not get asked. What each one changes
     is documented at the point it is read, in derive() and focusWeights().

     Inserting questions changes the index-based share encoding, which is
     why the UI's share parameter carries a version (rf2) — an old link
     falls back to a fresh quiz instead of mis-decoding.
     --------------------------------------------------------------------- */

  function has(answers, key, value) {
    var v = answers[key];
    if (v == null) return false;
    return Array.isArray(v) ? v.indexOf(value) !== -1 : v === value;
  }

  function track(name) {
    return function (a) { return has(a, 'track', name); };
  }

  var QUESTIONS = [
    // --- routing -------------------------------------------------------
    {
      key: 'track', type: 'multi', min: 1, when: null,
      values: ['skin', 'body', 'hair', 'scent', 'mind'],
      // Used by the UI to warn honestly when a department is empty.
      deptOf: { skin: 'skin', body: 'body', hair: 'hair', scent: 'fragrance', mind: 'mind' }
    },

    // --- face ----------------------------------------------------------
    // Two readings of the dry/oily axis. One reading misclassifies
    // dehydrated-but-oily skin as dry, which is the single most common
    // self-diagnosis error and the reason we do not simply ask "your type?".
    { key: 'feel',   type: 'single', when: track('skin'), values: ['tight', 'fine', 'shine', 'sting'] },
    { key: 'shine',  type: 'single', when: track('skin'), values: ['dry', 'same', 'tzone', 'allover'] },
    { key: 'react',  type: 'single', when: track('skin'), values: ['rarely', 'sometimes', 'often', 'unknown'] },
    { key: 'concern', type: 'single', when: track('skin'),
      values: ['calm', 'hydration', 'congestion', 'tone', 'texture', 'firmness', 'maintain'] },

    /* --- concern follow-ups ---------------------------------------------
       One per concern that genuinely divides into sub-cases wanting
       different answers. Texture, firmness and maintain get none — we
       could not name a follow-up whose answer would change the output,
       and that is the bar. Each is gated on the concern itself, so the
       question set adapts to what the visitor said. */
    { key: 'tone_kind', type: 'single',
      when: function (a) { return has(a, 'track', 'skin') && a.concern === 'tone'; },
      values: ['marks', 'dull', 'sun'] },
    { key: 'congestion_zone', type: 'single',
      when: function (a) { return has(a, 'track', 'skin') && a.concern === 'congestion'; },
      values: ['tzone', 'jaw', 'all'] },
    { key: 'dry_feel', type: 'single',
      when: function (a) { return has(a, 'track', 'skin') && a.concern === 'hydration'; },
      values: ['always', 'afterwash', 'seasonal'] },
    { key: 'calm_trigger', type: 'single',
      when: function (a) { return has(a, 'track', 'skin') && a.concern === 'calm'; },
      values: ['products', 'weather', 'nopattern'] },

    { key: 'sun',    type: 'single', when: track('skin'), values: ['low', 'mid', 'high'] },

    // --- body ----------------------------------------------------------
    { key: 'body_state', type: 'single', when: track('body'),
      values: ['dry', 'fine', 'bumpy', 'blemish', 'tone'] },

    // --- hair ----------------------------------------------------------
    { key: 'scalp',      type: 'single', when: track('hair'), values: ['oily', 'flaky', 'dry', 'fine'] },
    { key: 'hair_state', type: 'single', when: track('hair'), values: ['damaged', 'dry', 'fine', 'healthy'] },

    // --- scent ---------------------------------------------------------
    { key: 'scent_use',  type: 'single', when: track('scent'), values: ['light', 'hair', 'skip'] },

    // --- shared, deliberately last ------------------------------------
    // `steps` before `shelf`: `shelf` is the most effortful question in the
    // set and belongs after the user is committed. It is also the closest
    // thing here to health-adjacent data, so it is optional and late.
    { key: 'steps', type: 'single', when: track('skin'), values: ['three', 'five', 'all'] },
    {
      key: 'shelf', type: 'multi', optional: true, when: track('skin'),
      values: ['retinoid', 'acid', 'vitc', 'bpo', 'rx', 'none'], exclusive: 'none'
    },
    /* Frequency of the exfoliating load. Only asked of someone who just
       told us they own one, so the follow-up reads as listening rather
       than as a longer form. Frequency decides more than strength does:
       the same acid is a routine at twice a week and a problem nightly,
       and the over-exfoliation signature in derive() was previously blind
       to the difference. */
    { key: 'shelf_freq', type: 'single',
      when: function (a) {
        return has(a, 'track', 'skin') &&
               (has(a, 'shelf', 'retinoid') || has(a, 'shelf', 'acid') || has(a, 'shelf', 'bpo'));
      },
      values: ['light', 'most', 'daily'] },
    {
      key: 'avoid', type: 'multi', optional: true,
      when: function (a) {
        return has(a, 'track', 'skin') || has(a, 'track', 'body') ||
               has(a, 'track', 'hair') || has(a, 'track', 'scent');
      },
      values: ['fragrance', 'alcohol', 'none'], exclusive: 'none'
    },
    { key: 'stage', type: 'single', when: null, values: ['new', 'restart', 'prune'] },

    /* Free text. Runs LAST, is always optional, and only appears when the
       section has an AI endpoint configured — a free-text box that nothing
       reads is a box that wastes the visitor's time and collects personal
       data for no purpose. The UI drops it when `ai.endpoint` is blank. */
    { key: 'note', type: 'text', optional: true, aiOnly: true, when: null, values: [] }
  ];

  /* Which questions actually run, given what has been answered so far.
     `opts.ai` false drops the free-text question entirely. */
  function plannedQuestions(answers, opts) {
    var aiOn = !!(opts && opts.ai);
    var out = [];
    for (var i = 0; i < QUESTIONS.length; i++) {
      var q = QUESTIONS[i];
      if (q.aiOnly && !aiOn) continue;
      if (!q.when || q.when(answers)) out.push(q);
    }
    return out;
  }

  /* The finder can produce a result from partial answers. This is what makes
     "show me what you have so far" safe to offer from question five onward —
     abandonment stops being a loss. */
  function canResolve(answers) {
    if (!answers.track || !answers.track.length) return false;
    if (has(answers, 'track', 'skin') && !answers.concern) return false;
    if (has(answers, 'track', 'body') && !answers.body_state) return false;
    if (has(answers, 'track', 'hair') && !answers.scalp) return false;
    return true;
  }

  /* ---------------------------------------------------------------------
     2. Profile derivation.
     --------------------------------------------------------------------- */

  function derive(answers) {
    var a = answers || {};
    var tracks = a.track || [];

    // --- dry/oily axis: two signals, weighted -------------------------
    var oil = 0;
    if (a.feel === 'tight') oil -= 2;
    if (a.feel === 'shine') oil += 2;
    if (a.shine === 'dry') oil -= 2;
    if (a.shine === 'tzone') oil += 1;
    if (a.shine === 'allover') oil += 3;

    var type = 'normal';
    if (a.shine === 'tzone' && a.feel !== 'shine') type = 'combination';
    else if (oil >= 3) type = 'oily';
    else if (oil <= -2) type = 'dry';
    else if (oil >= 1) type = 'combination';

    // --- sensitivity ---------------------------------------------------
    var sensitivity = 'moderate';
    if (a.react === 'rarely') sensitivity = 'low';
    else if (a.react === 'often') sensitivity = 'high';
    else if (a.react === 'unknown') sensitivity = 'unknown';

    /* Follow-up correction: "my skin flares and I can't see a pattern" is
       not low-risk skin, whatever the react answer said. A visitor can
       honestly answer "almost never" to trying NEW things while their skin
       still flares on its own schedule — the two questions measure
       different things, and the cautious reading wins. Never downgrades. */
    if (a.calm_trigger === 'nopattern' && sensitivity === 'low') sensitivity = 'moderate';

    // --- barrier state -------------------------------------------------
    // "Unsettled", never "damaged" or "compromised". We are describing what
    // the customer told us, not assessing tissue.
    var actives = (a.shelf || []).filter(function (s) {
      return s === 'retinoid' || s === 'acid' || s === 'vitc' || s === 'bpo';
    });

    /* --- over-exfoliation ---------------------------------------------
       The most common self-inflicted problem in this category, and the one
       a shop has the least commercial incentive to name. Michelle Wong
       (PhD chem, Lab Muffin) describes the mechanism plainly: "If you
       exfoliate too much, you end up with a thinner stratum corneum with
       less layers. It's like you're giving yourself a mild chemical burn."
       Her first instruction is not a product: "The first thing to do is to
       stop sanding back what barrier you have left - in other words, stop
       using exfoliating products." Recovery, in the same piece, "can take
       weeks or months".
       https://labmuffin.com/how-to-fix-your-messed-up-skin-barrier/

       So we look for the signature rather than waiting for the visitor to
       volunteer the diagnosis they do not have the vocabulary for: an
       exfoliating load on the shelf, plus skin that is complaining. When
       both are true, the answer is subtraction, and the engine says so
       before it shows anything for sale.

       `vitc` is deliberately NOT counted here. It is an active with its own
       irritation potential, but it does not thin the stratum corneum by
       removing it, and folding it in would inflate this signal on shelves
       that are not actually over-exfoliated. */
    var exfoliants = actives.filter(function (s) {
      return s === 'retinoid' || s === 'acid' || s === 'bpo';
    });
    var overExfoliating =
      (exfoliants.length >= 1 && a.feel === 'sting') ||
      (exfoliants.length >= 1 && a.feel === 'tight' && a.react === 'often') ||
      (exfoliants.length >= 2 && (a.feel === 'tight' || a.react === 'often' || a.react === 'sometimes')) ||
      /* The frequency follow-up sharpens the signature. Daily use of any
         exfoliating step plus a skin already complaining is the classic
         presentation, and it used to slip through when the shelf held only
         ONE exfoliant and the reaction was mild — one acid, used nightly,
         on skin that is "tight" and reacts "once in a while" is exactly
         the case Wong's piece describes, and the old signature needed
         either two exfoliants or an "often" to see it. */
      (exfoliants.length >= 1 && a.shelf_freq === 'daily' &&
        (a.feel === 'tight' || a.react === 'sometimes' || a.react === 'often'));

    var unsettled =
      a.feel === 'sting' ||
      a.react === 'often' ||
      overExfoliating ||
      (actives.length >= 2 && (a.feel === 'tight' || a.react === 'sometimes'));
    var barrier = unsettled ? 'unsettled' : 'settled';

    /* --- sensitised, or sensitive? ------------------------------------
       These are not the same thing and the difference decides the advice.

       The IFSI position paper (Misery et al., Acta Derm Venereol 2017)
       defines sensitive skin as "a syndrome defined by the occurrence of
       unpleasant sensations (stinging, burning, pain, pruritus, and
       tingling sensations) in response to stimuli that normally should not
       provoke such sensations", adding that those sensations "cannot be
       explained by the lesions attributable to any skin disease" and that
       it "is defined mostly on the patients' complaints and cannot be
       precisely confirmed by any objective measurements".
       https://www.medicaljournals.se/acta/content/html/10.2340/00015555-2397

       Two things follow, and both are load-bearing here.

       First, a self-reported description is the only instrument that exists
       for this, which is exactly what a quiz collects. We are not
       substituting for a measurement; there isn't one.

       Second, the definition covers skin that reacts to things that should
       not provoke a reaction. It does not cover skin that is reacting to an
       acid and a retinoid used four nights a week, because those provoke
       reactions in almost anyone at enough dose. That is a different state
       with a different answer: the constitutional case wants gentler
       products indefinitely, the sensitised case wants fewer products for a
       while and then usually goes back to normal.

       Telling someone their skin is not actually sensitive - it is just
       being treated badly - is the single most useful sentence this tool
       can produce, and it sells nothing. */
    var sensitivityKind = null;
    if (a.react === 'unknown') sensitivityKind = 'unestablished';
    else if (overExfoliating || (unsettled && exfoliants.length >= 1)) sensitivityKind = 'sensitised';
    else if (a.react === 'often' && actives.length === 0) sensitivityKind = 'constitutional';

    // --- focus ---------------------------------------------------------
    // A skin that is reacting outranks whatever the customer picked. This is
    // the one place the tool openly overrules the user, and it says so.
    var focus = a.concern || 'maintain';
    var focusOverridden = false;
    if (unsettled && focus !== 'calm') { focus = 'calm'; focusOverridden = true; }

    /* The concern follow-up, resolved against the FINAL focus rather than
       the stated concern. When the engine overrides the focus to calm, a
       tone or congestion detail no longer describes the thing being worked
       on, and weighting by it would smuggle the overridden concern back in
       through the scoring. */
    var detail = null;
    if (focus === 'tone') detail = a.tone_kind || null;
    else if (focus === 'congestion') detail = a.congestion_zone || null;
    else if (focus === 'hydration') detail = a.dry_feel || null;
    else if (focus === 'calm') detail = a.calm_trigger || null;

    // --- step budget ---------------------------------------------------
    var budget = 3;
    if (a.steps === 'five') budget = 5;
    else if (a.steps === 'all') budget = 9;

    return {
      tracks: tracks,
      type: type,
      barrier: barrier,
      sensitivity: sensitivity,
      sensitivityKind: sensitivityKind,
      focus: focus,
      focusOverridden: focusOverridden,
      unsettled: unsettled,
      overExfoliating: overExfoliating,
      exfoliants: exfoliants,
      /* The one answer in the set that most often points somewhere we are
         not qualified to go. Kept as its own flag rather than folded into
         `unsettled`, because it drives an escalation and not a product
         decision, and the two must not be able to cancel each other out. */
      stingsOnWater: a.feel === 'sting',
      detail: detail,
      activeFreq: a.shelf_freq || null,
      calmTrigger: a.calm_trigger || null,
      budget: budget,
      sun: a.sun || null,
      shelf: a.shelf || [],
      actives: actives,
      onPrescription: has(a, 'shelf', 'rx'),
      avoid: (a.avoid || []).filter(function (x) { return x !== 'none'; }),
      stage: a.stage || 'restart',
      body: a.body_state || null,
      scalp: a.scalp || null,
      hair: a.hair_state || null,
      scent: a.scent_use || null
    };
  }

  /* ---------------------------------------------------------------------
     3. Concern weights per slot.

     Maps the derived focus onto the catalogue's own `concern:` tags. The
     catalogue is the source of truth — if a product is retagged, the finder
     follows it without a code change.
     --------------------------------------------------------------------- */

  /* The `beginner` tag no longer carries a courtesy point in the calm,
     hydration and tone vectors. It used to, and the arithmetic made it a
     universal tiebreak: a product tagged beginner strictly dominated any
     same-concern stablemate in nearly every profile, which is how one
     moisturiser ended up winning 92% of all simulated routines while four
     others could never be reached at all. Beginner-friendliness still
     counts exactly where it means something: the maintain focus, and the
     explicit stage-new bonus in pick().

     `firm` is the firmness concern's own tag (on the collagen masks and
     the retinal eye serum). It used to borrow tone+dehydrated, which meant
     a brightening serum outranked an actual firmness product for the one
     visitor who asked about firmness. */
  var FOCUS_WEIGHTS = {
    calm:      { sensitive: 5, dehydrated: 2 },
    hydration: { dehydrated: 5, sensitive: 2 },
    congestion:{ blemish: 5, texture: 2 },
    tone:      { tone: 5 },
    texture:   { texture: 5, dehydrated: 2 },
    firmness:  { firm: 5, tone: 2, dehydrated: 2 },
    maintain:  { beginner: 5, sensitive: 1, dehydrated: 1 }
  };

  var BODY_WEIGHTS = {
    dry:     { dehydrated: 5, sensitive: 3 },
    fine:    { dehydrated: 3, sensitive: 2 },
    bumpy:   { texture: 5, blemish: 2 },
    blemish: { blemish: 5, texture: 2 },
    tone:    { tone: 5 }
  };

  /* The concern follow-up, applied to the scoring. This is where "different
     questions" earns its keep: each detail below moves real weight, so two
     visitors with the same concern and different follow-up answers get
     rankings that can genuinely differ. Anything not listed keeps the base
     weights — the follow-up's remaining output is a rule, applied in
     rules(), and the two must not double-count. */
  function focusWeights(profile) {
    var base = FOCUS_WEIGHTS[profile.focus] || FOCUS_WEIGHTS.maintain;
    var d = profile.detail;
    if (!d) return base;

    var out = {}, k;
    for (k in base) if (base.hasOwnProperty(k)) out[k] = base[k];

    if (profile.focus === 'tone') {
      // Dullness is usually a water problem wearing a tone costume; marks
      // respond to gentle resurfacing alongside the sunscreen that does
      // most of the work. Sun-darkening patches keep the base weights —
      // their answer is the SPF pair of rules, not a different serum.
      if (d === 'dull') return { tone: 3, dehydrated: 3, beginner: 1 };
      if (d === 'marks') out.texture = (out.texture || 0) + 1;
    } else if (profile.focus === 'congestion') {
      // Jaw-and-cheeks congestion is the pattern most often already
      // irritated by the scrubbing meant to fix it: bias gentle. T-zone
      // congestion tolerates — and benefits from — the texture step.
      if (d === 'jaw') return { blemish: 4, sensitive: 2, texture: 1 };
      if (d === 'tzone') out.texture = (out.texture || 0) + 1;
    } else if (profile.focus === 'hydration') {
      // Tight straight after washing points at the cleanser stripping, so
      // gentleness outranks humectant load — and rule_cleanser_first says
      // the quiet part out loud.
      if (d === 'afterwash') out.sensitive = (out.sensitive || 0) + 2;
    } else if (profile.focus === 'calm') {
      // Weather-triggered reactivity leans on barrier support: the
      // dehydrated tag marks the water-holding, film-forming products.
      if (d === 'weather') out.dehydrated = (out.dehydrated || 0) + 2;
    }
    return out;
  }

  function tagsOf(product) { return product.tags || []; }

  function hasTag(product, tag) { return tagsOf(product).indexOf(tag) !== -1; }

  function concernsOf(product) {
    return tagsOf(product)
      .filter(function (t) { return t.indexOf('concern:') === 0; })
      .map(function (t) { return t.slice(8); });
  }

  /* ---------------------------------------------------------------------
     3b. Catalogue coverage.

     31 of 48 products are drafts as of August 2026, and the gaps are whole
     departments: all four tools, both shampoos, both hair treatments, the
     hair oil, the hair serum. A step with NO product in the catalogue at all
     is a different fact from a step where we stock things and none of them
     suit this visitor, and the two must not share a sentence. Conflating
     them makes an empty shelf read as a rejection of the visitor, which is
     both untrue and the most damaging available reading.
     --------------------------------------------------------------------- */

  function coverage(catalogue) {
    var seen = {};
    var pool = catalogue || [];
    for (var i = 0; i < pool.length; i++) {
      if (pool[i].av === false) continue;
      var tags = tagsOf(pool[i]);
      for (var j = 0; j < tags.length; j++) {
        if (tags[j].indexOf('step:') === 0) seen[tags[j].slice(5)] = true;
        if (tags[j].indexOf('dept:') === 0) seen['dept:' + tags[j].slice(5)] = true;
      }
    }
    return seen;
  }

  /* Which of the five tracks we cannot serve at all today, as track keys, so
     the routing question can be honest before the visitor invests ten
     answers in a department that has no shelf behind it. */
  function unstockedTracks(catalogue) {
    var cov = coverage(catalogue);
    var map = QUESTIONS[0].deptOf;
    var out = [];
    for (var k in map) {
      if (map.hasOwnProperty(k) && !cov['dept:' + map[k]]) out.push(k);
    }
    return out;
  }

  /* ---------------------------------------------------------------------
     4. Eligibility — the honest filter.

     THE "FREE FROM" PROBLEM, AND WHY THIS CHANGED.

     `custom.fragrance_free` is not populated on any product in the live
     catalogue. The previous version treated an unknown value as a hard NO,
     which is right as a CLAIMS rule and ruinous as a SHOP rule: a visitor who
     ticked "avoid fragrance" — a common and entirely reasonable answer — had
     every slot removed and was shown "we don't have anything to sell you
     today". Simulated across 20,000 answer permutations against the live
     catalogue, that path fired on roughly two fifths of all sessions.

     Both readings of the rule are wrong, in opposite directions:
       - fail open   → we imply "fragrance-free" without substantiation. The
         Commission's Technical Document on cosmetic claims (Annex III) allows
         a "free from" claim only where no ingredient performs that function.
       - fail closed → we tell the visitor we sell nothing, which is false,
         and we spend the brand's most serious sentence on a metafield.

     There is a third option and it is the honest one: never make the claim,
     never pretend the shelf is empty. A product whose formulation we have not
     confirmed is offered with an explicit unverified caveat attached, in
     plain words, so the visitor can read the ingredient list themselves
     before buying. A product we positively know contains fragrance is still
     removed outright, and says so.

     `unverifiedPolicy: 'exclude'` restores the strict behaviour in one
     setting, and is the correct setting the day the metafields are
     populated. Until then, `caveat` is both more truthful and more useful.
     --------------------------------------------------------------------- */

  function eligibility(product, profile, policy) {
    if (product.av === false) return { ok: false, why: 'unavailable' };

    var strict = policy && policy.unverifiedPolicy === 'exclude';
    var caveats = [];

    if (profile.avoid.indexOf('fragrance') !== -1) {
      if (product.ff === false) return { ok: false, why: 'has_fragrance' };
      if (product.ff !== true) {
        if (strict) return { ok: false, why: 'fragrance_unknown' };
        caveats.push('fragrance_unverified');
      }
    }
    if (profile.avoid.indexOf('alcohol') !== -1) {
      if (product.al === false) return { ok: false, why: 'has_alcohol' };
      if (product.al !== true) {
        if (strict) return { ok: false, why: 'alcohol_unknown' };
        caveats.push('alcohol_unverified');
      }
    }

    // Strength gate. `strong` = anything acid-bearing or physically abrasive.
    // Populated from the `custom.strength` metafield; absent defaults to
    // 'moderate' so we never silently hand an acid to a reacting skin. This
    // one stays fail-closed with no caveat escape: the cost of being wrong
    // here is an irritated customer, not an unsubstantiated adjective.
    var strength = product.str || 'moderate';
    if (strength === 'strong') {
      if (profile.unsettled) return { ok: false, why: 'settle_first' };
      if (profile.sensitivity === 'high') return { ok: false, why: 'too_strong' };
      if (profile.actives.length >= 1) return { ok: false, why: 'already_exfoliating' };
      /* A first routine does not start with an acid. Someone with no
         routine history has no idea yet how their skin responds to
         anything, and the cheapest way to find out is not with the
         strongest thing on the shelf. This one costs us the sale of the
         highest-margin item in the department to the customer most likely
         to buy it, which is roughly the point. */
      if (profile.stage === 'new') return { ok: false, why: 'too_strong' };
    }

    return { ok: true, caveats: caveats };
  }

  /* Score, then choose. Ties break to the cheaper product — the brand
     position is that a cheaper product doing the same job wins.

     `essential` marks a step that is part of any working routine regardless
     of what the visitor is trying to change: cleanse, moisturise, sun
     protection. Those must not be concern-gated. Somebody focused on
     congestion still needs a moisturiser, and no moisturiser in the
     catalogue carries a `concern:blemish` tag — scoring alone would drop the
     step and produce a routine of one cleanser.

     Discretionary steps keep the strict rule: a zero score means nothing in
     the catalogue is actually aimed at this person, and recommending the
     least-bad option anyway is how shelves get built. We decline instead. */
  function pick(pool, stepTag, weights, profile, taken, essential, policy) {
    var best = null, bestScore = -1, bestCaveats = [], blocked = {}, eligible = [];
    var stocked = 0;

    for (var i = 0; i < pool.length; i++) {
      var p = pool[i];
      if (!hasTag(p, 'step:' + stepTag)) continue;
      if (p.av !== false) stocked++;                     // exists on the shelf at all
      if (taken.indexOf(p.h) !== -1) continue;           // never sell the same SKU twice

      var el = eligibility(p, profile, policy);
      if (!el.ok) { blocked[el.why] = (blocked[el.why] || 0) + 1; continue; }
      eligible.push({ p: p, caveats: el.caveats });

      var score = 0, cs = concernsOf(p);
      for (var j = 0; j < cs.length; j++) score += (weights[cs[j]] || 0);
      if (profile.stage === 'new' && cs.indexOf('beginner') !== -1) score += 2;

      /* Skin-type fit. The `type:` tags are the axis the dry-skin / oily-skin
         smart collections are built on, and until August 2026 the engine
         never read them — the quiz spent two questions deriving a type and
         then chose products as if it hadn't asked. A product tagged for the
         visitor's derived type outranks one that isn't; a product with no
         type tags is neutral rather than penalised, so a genuinely
         type-agnostic product (a pimple patch) still competes everywhere.

         A SPECIALIST outranks a generalist on its own ground: a product
         tagged for exactly one type, matching this visitor, takes one more
         point than a product that suits three types equally. This is what
         lets a dedicated dry-skin cream beat a cheaper suits-most cream
         for the visitor whose answers actually read dry — without it, the
         two tie on tags and the price tiebreak retires the specialist from
         the catalogue permanently. */
      var typeTags = 0;
      for (var tt = 0; tt < tagsOf(p).length; tt++) {
        if (tagsOf(p)[tt].indexOf('type:') === 0) typeTags++;
      }
      if (hasTag(p, 'type:' + profile.type)) score += (typeTags === 1 ? 3 : 2);

      /* "I don't know, I haven't tried much" is scored as cautiously as
         "often". The question's own supporting line promises exactly that —
         "Then we'll assume the cautious middle, and say so" — and until now
         the engine did not keep it: `unknown` produced the same ranking as
         `moderate`, so a first-timer who had never reacted to anything
         because they had never used anything could be handed the more
         assertive of two products on a concern-tag tiebreak.

         Somebody with no reaction history is not low risk. They are
         unmeasured, and the two are opposite. The IFSI definition is
         explicit that sensitive skin "is defined mostly on the patients'
         complaints and cannot be precisely confirmed by any objective
         measurements" — so an absent complaint is an absent measurement,
         not a negative result. */
      var cautious = profile.sensitivity === 'high' || profile.sensitivity === 'unknown';
      if (cautious && cs.indexOf('sensitive') !== -1) score += 2;

      // A gentle formulation is a mild positive on every step, so an
      // essential slot has something to rank on when the concern doesn't
      // match — and a decisive one when the answers ask us to be careful.
      if (p.str === 'gentle') score += cautious ? 3 : 1;
      // A product we can positively confirm against the visitor's avoid list
      // outranks one we have merely not checked. This is the only place the
      // missing metafields affect ORDER rather than inclusion, and it means
      // the finder improves on its own the day they are populated.
      if (el.caveats.length === 0) score += 1;

      if (score > bestScore || (score === bestScore && best && p.p < best.p)) {
        best = p; bestScore = score; bestCaveats = el.caveats;
      }
    }

    // Nothing in the catalogue carries this step tag at all. That is a stock
    // fact about us, not a judgement about the visitor, and the result page
    // must be able to tell the two apart.
    if (stocked === 0) return { product: null, blocked: blocked, score: -1, notStocked: true };

    if (best && bestScore > 0) {
      return { product: best, caveats: bestCaveats, blocked: blocked, score: bestScore };
    }

    if (essential && eligible.length) {
      // Nothing matched the concern, but the step still has to happen.
      // Take the cheapest thing we would be happy to sell anyone.
      var cheapest = eligible.reduce(function (a, b) { return b.p.p < a.p.p ? b : a; });
      return { product: cheapest.p, caveats: cheapest.caveats, blocked: blocked, score: 0 };
    }

    return { product: null, blocked: blocked, score: bestScore };
  }

  /* ---------------------------------------------------------------------
     5. Slot plan.

     Slots are emitted in priority order, then trimmed to the step budget.
     Everything trimmed is reported, with a reason — the "what we left out"
     panel is the product, not a footnote.

     TWO ORDERS, AND THEY ARE NOT THE SAME ORDER.

     `priority` decides what survives the step budget. `order` decides what
     the visitor is told to do first. Conflating them is how a routine ends
     up printed as "1. Cleanse  2. Moisturise  3. Sunscreen  4. Serum",
     numbered as if it were a sequence, which then reads as an instruction
     to put a serum on top of a moisturiser.

     Application order for the face is thin to thick, with sun protection
     last on the skin in the morning — a sunscreen is a film, and anything
     spread over it disturbs the film. Water step before treatment before
     moisturiser is the same principle one layer down.

     Body and hair get their own low-collision ranges so a mixed routine
     still groups sensibly rather than interleaving a body lotion into the
     middle of the face steps.
     --------------------------------------------------------------------- */

  var APPLY_ORDER = {
    cleanse: 10, tone: 20, treat: 30, moisturise: 40, protect: 50,
    body_cleanse: 60, body_moisturise: 70,
    hair_cleanse: 80, hair_treat: 90,
    scent: 100, ritual: 110
  };

  function slotPlan(profile) {
    var slots = [];
    var t = profile.tracks;
    // One computation, four slots — the detail-adjusted weights must agree
    // across cleanse/protect/moisturise/treat or the routine argues with itself.
    var fw = focusWeights(profile);

    if (t.indexOf('skin') !== -1) {
      slots.push({ id: 'cleanse', step: 'cleanse', dept: 'skin', priority: 1, essential: true, weights: fw });

      /* Sunscreen is priority 1. Not "priority 1 when the visitor asked
         about pigmentation" — priority 1, always, for everyone on the face
         track, ahead of every serum in the catalogue.

         This used to be conditional: 1 for tone and firmness, 2 if the
         visitor said they were outdoors a lot, 3 otherwise. That ranking
         was a description of demand rather than of value, and at a three-
         step budget it let a treatment step outrank the only step with an
         effect on how skin looks in ten years. Daylight exposure changes
         how much sunscreen matters, not whether it does; someone who
         answers "barely any - indoors, curtains shut" is describing a
         typical day, not every day.

         It is also the cheapest thing in the routine, which is the tell
         that this ranking is not being set by margin. */
      slots.push({ id: 'protect', step: 'protect', dept: 'skin', priority: 1, essential: true, weights: fw });

      slots.push({ id: 'moisturise', step: 'moisturise', dept: 'skin', priority: 2, essential: true, weights: fw });

      // The treat slot is suppressed outright when the skin is reacting or a
      // prescriber is already involved.
      if (!profile.unsettled && !profile.onPrescription) {
        slots.push({ id: 'treat', step: 'treat', dept: 'skin', priority: 4, weights: fw });
      } else {
        slots.push({
          id: 'treat', step: 'treat', dept: 'skin', priority: 4,
          weights: fw,
          suppressed: profile.onPrescription ? 'prescription' : 'settle_first'
        });
      }

      // A toner/essence only earns a slot when the user said they would do
      // five steps AND the water step has a job: water retention, calming,
      // or — since August 2026 — tone. A brightening water step for the
      // tone focus is an ordinary K-beauty routine, and suppressing the
      // slot there meant a toner tagged concern:tone could never surface
      // for the one visitor it was tagged for.
      var toneEarns = (profile.focus === 'hydration' || profile.type === 'dry' ||
                       profile.focus === 'calm' || profile.focus === 'tone');
      slots.push({
        id: 'tone', step: 'tone', dept: 'skin', priority: 5,
        weights: fw,
        suppressed: toneEarns ? null : 'duplicates_moisturiser'
      });
    }

    if (t.indexOf('body') !== -1) {
      slots.push({ id: 'body_moisturise', step: 'body-moisturise', dept: 'body', priority: 2, essential: true, weights: BODY_WEIGHTS[profile.body] || BODY_WEIGHTS.fine });
      slots.push({ id: 'body_cleanse', step: 'body-cleanse', dept: 'body', priority: 4, weights: BODY_WEIGHTS[profile.body] || BODY_WEIGHTS.fine });
    }

    if (t.indexOf('hair') !== -1) {
      slots.push({ id: 'hair_cleanse', step: 'hair-cleanse', dept: 'hair', priority: 2, essential: true, weights: scalpWeights(profile.scalp) });
      slots.push({
        id: 'hair_treat', step: 'hair-treat', dept: 'hair', priority: 3,
        weights: { damage: 5, scent: 0 },
        // Protein on undamaged hair makes it brittle — the La'dor product's own
        // `skip_if` says exactly that. We honour our own copy.
        suppressed: profile.hair === 'healthy' ? 'healthy_hair' : null
      });
    }

    if (t.indexOf('scent') !== -1 && profile.scent !== 'skip') {
      slots.push({ id: 'scent', step: 'finish', dept: 'fragrance', priority: 6, weights: { scent: 5, damage: profile.scent === 'hair' ? 3 : 0 } });
    }

    if (t.indexOf('mind') !== -1) {
      slots.push({ id: 'ritual', step: 'ritual', dept: 'mind', priority: 6, weights: { beginner: profile.stage === 'new' ? 5 : 1 } });
    }

    slots.sort(function (x, y) { return x.priority - y.priority; });
    return slots;
  }

  /* Application order, used only when the surviving steps are drawn as a
     numbered list. Anything the map does not know about sorts last rather
     than to the front, so adding a slot without updating APPLY_ORDER is
     visibly wrong rather than silently wrong. */
  function applyOrder(slotId) {
    return APPLY_ORDER[slotId] == null ? 999 : APPLY_ORDER[slotId];
  }

  function scalpWeights(scalp) {
    if (scalp === 'oily')  return { scalp: 5, texture: 2 };
    if (scalp === 'flaky') return { scalp: 4, texture: 1 };
    if (scalp === 'dry')   return { scalp: 2 };
    return { scalp: 3 };
  }

  /* ---------------------------------------------------------------------
     6. Advisory rules — free expertise, no product attached.

     Sequencing and escalation. These are the sentences that cost us a sale
     and buy the trust the whole store runs on.
     --------------------------------------------------------------------- */

  function rules(profile) {
    var out = [], s = profile.shelf;
    function add(k) { if (out.indexOf(k) === -1) out.push(k); }

    /* ESCALATION FIRST, ALWAYS.

       Not because it is the most likely explanation, but because it is the
       one we are least qualified to handle, and because a shop that buries
       "go and see someone" under a shopping list has not really said it.
       Stinging on contact with plain water is the answer that most often
       means something a cosmetic does not address. We describe the answer
       they gave us and name who to ask. We do not name a condition, and we
       do not guess at one — see the vocabulary note at the top of this
       file. */
    if (profile.stingsOnWater) add('rule_escalate');

    if (profile.onPrescription) add('rule_prescription');

    /* Over-exfoliation before anything else that is ours to fix. If this
       fires, every other line on the page is subordinate to it. */
    if (profile.overExfoliating) add('rule_over_exfoliation');
    if (profile.sensitivityKind === 'sensitised') add('rule_sensitised_not_sensitive');

    /* The frequency follow-up's own line. Daily use of an exfoliating step
       gets the frequency advice even when the skin has not started
       complaining yet — that is the visit where the sentence is cheapest. */
    if (profile.activeFreq === 'daily' && profile.exfoliants.length >= 1) add('rule_active_frequency');

    /* Dryness that appears right after washing is a cleanser problem until
       proven otherwise, and saying so outranks selling a humectant. */
    if (profile.focus === 'hydration' && profile.detail === 'afterwash') add('rule_cleanser_first');

    /* "It flares when I try something new" is the one-at-a-time rule in the
       visitor's own words. Say it back to them even when their react answer
       alone would not have triggered it. */
    if (profile.calmTrigger === 'products') add('rule_one_at_a_time');

    // Real irritation risk, well documented.
    if (s.indexOf('retinoid') !== -1 && s.indexOf('acid') !== -1) add('rule_retinoid_acid');
    if (s.indexOf('retinoid') !== -1 && s.indexOf('bpo') !== -1)  add('rule_retinoid_bpo');
    // Internet folklore we refuse to repeat. Saying so is the point.
    if (s.indexOf('vitc') !== -1) add('rule_vitc_niacinamide_myth');
    if (profile.actives.length >= 2) add('rule_too_many_actives');
    // Retinoids are a pregnancy question. We do not ask; we say it to everyone
    // who owns one, which costs the user no privacy at all.
    if (s.indexOf('retinoid') !== -1) add('rule_retinoid_pregnancy');
    /* Two things almost every retinoid owner has been told wrong: that you
       start with the strongest one you can get, and that getting worse means
       it is working. Both cost money and skin. */
    if (s.indexOf('retinoid') !== -1) add('rule_retinoid_tolerance');
    if (s.indexOf('retinoid') !== -1) add('rule_retinoid_flare');
    if (s.indexOf('acid') !== -1 || s.indexOf('retinoid') !== -1) add('rule_spf_with_actives');

    if (profile.unsettled) add('rule_settle_first');
    if (profile.unsettled) add('rule_barrier_recovery');
    if (profile.sensitivity === 'high' || profile.sensitivity === 'unknown') add('rule_one_at_a_time');

    /* Photoprotection is the highest-value thing on this page and the thing
       most often done wrong, so it is stated to everybody on the face track
       rather than kept for people who asked about pigmentation. The failure
       is almost never the product. */
    if (profile.tracks.indexOf('skin') !== -1) add('rule_spf_amount');
    if (profile.tracks.indexOf('skin') !== -1 &&
        (profile.sun === 'high' || profile.focus === 'tone' || profile.focus === 'firmness')) {
      add('rule_spf_number');
    }
    // Concentration honesty, on the one focus where our own shelf has a
    // product whose whole argument is an ingredient percentage.
    if (profile.focus === 'tone') add('rule_niacinamide_percent');

    // Persistent scalp flaking with itch is a medical matter. We name the
    // pharmacist, not a product.
    if (profile.scalp === 'flaky') add('rule_scalp_persistent');
    if (profile.body === 'bumpy') add('rule_body_bumps_expectations');
    if (profile.focus === 'tone') add('rule_tone_takes_months');
    if (profile.focus === 'firmness') add('rule_firmness_limits');
    if (profile.focus === 'congestion') add('rule_congestion_persistent');

    return out;
  }

  /* ---------------------------------------------------------------------
     6b. Read mode — products attached to advice, never to a routine.

     THE PROBLEM THIS SOLVES.

     The two tools used to be separated by whether they sold anything. That
     is a distinction about us, not about the visitor, and it produced the
     worst of both: a page that gave someone a reading, told them what to
     change, and then made them go and find it themselves.

     The distinction is now about what each tool ANSWERS.

       build  "what should my routine be?"       -> an ordered routine
       read   "what is my skin doing, and what
               should I change about it?"        -> the assessment, the
                                                    behaviour first, and a
                                                    product only where one
                                                    genuinely helps

     So this function is deliberately not a routine builder with a lower
     cap. It starts from zero and has to be argued up. A slot is only filled
     when a specific, stated piece of reasoning says a product is the
     limiting factor — and it uses `pick()` with `essential = false`, so a
     zero concern score returns nothing rather than the least-bad thing on
     the shelf. Two is the ceiling and zero is a completely ordinary result.

     WHAT IS DELIBERATELY NOT HERE.

     There is no rule that fires on "the visitor did not mention owning a
     cleanser", because we did not ask, and inferring a gap from silence is
     how a two-product answer becomes a five-product answer. There is no
     rule for `focus === 'congestion'` or `focus === 'texture'`: the honest
     answer to both, on this shelf, is behavioural — stop stripping, use one
     exfoliating step at most — and the advice panel already says it for
     free.
     --------------------------------------------------------------------- */

  function helpers(profile, catalogue, policy) {
    var out = [], taken = [], pool = catalogue || [];

    function argueFor(why, stepTag, weights) {
      if (out.length >= 2) return;
      var r = pick(pool, stepTag, weights, profile, taken, false, policy);
      if (!r.product) return;
      out.push({ why: why, slot: stepTag, step: stepTag, product: r.product, caveats: r.caveats || [] });
      taken.push(r.product.h);
    }

    if (profile.tracks.indexOf('skin') === -1) return out;

    /* 1. Sun protection, when their own answers have made it the binding
          constraint. Acids measurably increase how easily skin burns, and
          "evenness" is a goal that daily photoprotection contributes more
          to than any serum we sell — so on those paths the sunscreen is the
          honest recommendation and the brightening serum is not. */
    if (profile.exfoliants.length >= 1 ||
        profile.focus === 'tone' || profile.focus === 'firmness') {
      argueFor('helper_protect', 'protect', { sensitive: 3, beginner: 2, tone: 3, dehydrated: 1 });
    }

    /* 2. Skin that is reacting. The instruction is subtraction, and the
          copy attached to this says so: the four weeks do the work, and
          this is at most the one bland thing to use during them. We offer a
          moisturiser and never an active, because the whole finding is that
          there is already too much going on. */
    if (profile.unsettled) {
      argueFor('helper_settle', 'moisturise', { sensitive: 5, dehydrated: 3, beginner: 1 });
    } else if (profile.focus === 'hydration' && (profile.type === 'dry' || profile.type === 'normal')) {
      /* 3. Water-holding, and only when that is the stated problem, the
            skin is calm enough to add something, and the type reading
            agrees. An oily-but-dehydrated reading deliberately does not
            reach here — that one is usually a cleansing problem. */
      argueFor('helper_hydration', 'moisturise', { dehydrated: 5, sensitive: 2 });
    }

    return out;
  }

  /* ---------------------------------------------------------------------
     7. Plan — the whole thing.
     --------------------------------------------------------------------- */

  function plan(answers, catalogue, policy) {
    var profile = derive(answers);
    var pool = catalogue || [];
    var wanted = slotPlan(profile);

    var chosen = [], cuts = [], gaps = [], taken = [];
    var skinKept = 0;

    for (var i = 0; i < wanted.length; i++) {
      var slot = wanted[i];

      if (slot.suppressed) {
        cuts.push({ slot: slot.id, dept: slot.dept, reason: slot.suppressed });
        continue;
      }

      // The step budget applies to the face routine only — a body lotion is
      // not competing for the same two minutes as a serum.
      if (slot.dept === 'skin' && skinKept >= profile.budget) {
        cuts.push({ slot: slot.id, dept: slot.dept, reason: 'over_budget' });
        continue;
      }

      var res = pick(pool, slot.step, slot.weights || {}, profile, taken, slot.essential, policy);

      if (!res.product) {
        if (res.notStocked) {
          // We do not sell this yet. Tracked separately from a rejection so
          // the result page can offer to say when it lands, rather than
          // implying we looked at this visitor and found them unsuitable.
          var gap = { slot: slot.id, dept: slot.dept, reason: 'not_stocked_yet' };
          gaps.push(gap);
          cuts.push(gap);
        } else {
          cuts.push({
            slot: slot.id, dept: slot.dept,
            reason: dominant(res.blocked) || 'nothing_suitable',
            blocked: res.blocked
          });
        }
        continue;
      }

      chosen.push({
        slot: slot.id, step: slot.step, dept: slot.dept,
        product: res.product,
        caveats: res.caveats || []
      });
      taken.push(res.product.h);
      if (slot.dept === 'skin') skinKept++;
    }

    /* Trimmed by priority above; presented in application order here. See
       the two-orders note in section 5 — a numbered list is read as a
       sequence whether or not it was meant as one. */
    chosen.sort(function (x, y) { return applyOrder(x.slot) - applyOrder(y.slot); });

    var totalCents = chosen.reduce(function (n, c) { return n + (c.product.p || 0); }, 0);

    // Every caveat that ended up on the page, deduplicated, so the result can
    // explain each one once instead of repeating the explanation per product.
    var caveatKeys = [];
    chosen.forEach(function (c) {
      (c.caveats || []).forEach(function (k) {
        if (caveatKeys.indexOf(k) === -1) caveatKeys.push(k);
      });
    });

    var buyNothing = chosen.length === 0;
    var onlyGaps = false;
    if (buyNothing && cuts.length) {
      onlyGaps = true;
      for (var c2 = 0; c2 < cuts.length; c2++) {
        if (cuts[c2].reason !== 'not_stocked_yet') { onlyGaps = false; break; }
      }
    }

    /* Read mode's shortlist. Computed on every plan rather than only when
       the mode asks for it, so the two modes cannot drift apart: whatever
       the read page would offer is derivable from the same result object
       the build page renders, and a test can assert on both at once. */
    var helped = helpers(profile, pool, policy);

    return {
      profile: profile,
      items: chosen,
      helpers: helped,
      cuts: cuts,
      gaps: gaps,
      caveats: caveatKeys,
      rules: rules(profile),
      total: totalCents,
      // The outcome the brand exists to be able to reach.
      buyNothing: buyNothing,
      // ...and the outcome that is merely a stock fact, which must never be
      // dressed up as the one above.
      onlyGaps: onlyGaps,
      answers: answers
    };
  }

  function dominant(counts) {
    var best = null, n = 0;
    for (var k in counts) if (counts.hasOwnProperty(k) && counts[k] > n) { best = k; n = counts[k]; }
    return best;
  }

  /* ---------------------------------------------------------------------
     8. Verdict selection — which paragraph the result page leads with.
     --------------------------------------------------------------------- */

  function verdictKey(result) {
    var p = result.profile;
    if (result.onlyGaps)        return 'verdict_not_stocked';
    if (result.buyNothing)      return 'verdict_nothing';
    if (p.onPrescription)       return 'verdict_prescription';
    /* Over-exfoliation outranks the general "reacting" verdict and outranks
       the visitor's stated focus, because it is the one finding where the
       whole answer is a subtraction and where a shopping list actively
       misleads. It sits above `focusOverridden` for the same reason: if we
       are going to set aside what someone asked about, the more useful
       thing to lead with is why. */
    if (p.overExfoliating)      return 'verdict_over_exfoliation';
    if (p.focusOverridden)      return 'verdict_overridden';
    if (p.unsettled)            return 'verdict_unsettled';
    if (p.stage === 'prune')    return 'verdict_prune';
    if (p.stage === 'new')      return 'verdict_new';
    return 'verdict_standard';
  }

  /* ---------------------------------------------------------------------
     9. AI signal merge — the ONLY route from generated text to a product.

     The AI layer may read the visitor's free text and propose ANSWERS. It may
     not propose products, prices, ordering or exclusions, and it never sees
     the catalogue. Whatever it proposes is validated here against the same
     value vocabulary the quiz itself uses, and anything unrecognised is
     dropped in silence. The merged answers are then fed back through `plan()`
     unchanged, so every product on the page is still the output of a rule
     applied to an answer — auditable, reproducible, and explainable to a
     regulator without reference to a model.

     SAFETY DIRECTION — enforced here, not in the prompt.
     A prompt instruction is a request; this is a control. Signals may only
     move the result in the CONSERVATIVE direction:

       may set    feel=sting, react=often/unknown  (raise caution)
       may fill   a blank answer
       may add    shelf entries, avoid entries     (add constraints)
       may set    concern                          (change emphasis only)
       may lower  steps                            (fewer products)

       may NEVER  clear or downgrade an existing caution
       may NEVER  remove a shelf or avoid entry the visitor gave
       may NEVER  raise the step budget
       may NEVER  touch `track` — the visitor chose what they came for

     The worst a hallucinating or manipulated model can therefore do is make
     the finder recommend LESS. That is a failure mode this shop can live
     with; the reverse is not.
     --------------------------------------------------------------------- */

  var CAUTION_RANK = { rarely: 0, sometimes: 1, unknown: 2, often: 3 };
  var STEP_RANK = { three: 0, five: 1, all: 2 };
  /* `feel` is not a caution ladder by nature, but it becomes one once you ask
     which direction produces the gentler routine. `tight` and `sting` both
     pull toward hydration, gentler formulations and fewer actives; `shine`
     pulls toward oily-targeted products, which are the stronger ones. So the
     model may move a visitor UP this ladder and never down. That is what
     lets "my skin gets tight after cleansing" override a hurried tap on
     "feels fine" — the note is the more considered answer, and the direction
     it moves us in is the safe one. */
  var FEEL_RANK = { shine: 0, fine: 1, tight: 2, sting: 3 };

  function valuesFor(key) {
    for (var i = 0; i < QUESTIONS.length; i++) if (QUESTIONS[i].key === key) return QUESTIONS[i].values;
    return [];
  }

  function applySignals(answers, signals) {
    var out = {}, k;
    for (k in answers) if (answers.hasOwnProperty(k)) out[k] = answers[k];
    var applied = [];
    if (!signals || typeof signals !== 'object') return { answers: out, applied: applied };

    function allowed(key, value) {
      return typeof value === 'string' && valuesFor(key).indexOf(value) !== -1;
    }

    // feel — may only move UP the gentleness ladder, or fill a blank.
    if (allowed('feel', signals.feel)) {
      var curFeel = FEEL_RANK[out.feel] == null ? -1 : FEEL_RANK[out.feel];
      if (FEEL_RANK[signals.feel] > curFeel) { out.feel = signals.feel; applied.push('feel'); }
    }

    // shine — only fills a blank; never overrides what the visitor said.
    if (allowed('shine', signals.shine) && !out.shine) { out.shine = signals.shine; applied.push('shine'); }

    // react — may only increase caution.
    if (allowed('react', signals.react)) {
      var cur = CAUTION_RANK[out.react] == null ? -1 : CAUTION_RANK[out.react];
      if (CAUTION_RANK[signals.react] > cur) { out.react = signals.react; applied.push('react'); }
    }

    // concern — emphasis only. The engine still overrules it when the skin is
    // reacting, so this cannot unlock anything stronger.
    if (allowed('concern', signals.concern) && signals.concern !== out.concern) {
      out.concern = signals.concern; applied.push('concern');
    }

    // steps — may only shorten the routine.
    if (allowed('steps', signals.steps)) {
      var curStep = STEP_RANK[out.steps] == null ? 99 : STEP_RANK[out.steps];
      if (STEP_RANK[signals.steps] < curStep) { out.steps = signals.steps; applied.push('steps'); }
    }

    // shelf / avoid — additive only. Nothing the visitor declared is removed.
    ['shelf', 'avoid'].forEach(function (key) {
      var proposed = signals[key];
      if (!Array.isArray(proposed)) return;
      var vals = valuesFor(key);
      var cur = Array.isArray(out[key]) ? out[key].slice() : [];
      var added = false;
      proposed.forEach(function (v) {
        if (vals.indexOf(v) === -1) return;
        if (v === 'none') return;                    // never let the model clear a constraint
        if (cur.indexOf(v) === -1) { cur.push(v); added = true; }
      });
      if (added) {
        // "none" is exclusive and cannot survive an addition.
        out[key] = cur.filter(function (v) { return v !== 'none'; });
        applied.push(key);
      }
    });

    return { answers: out, applied: applied };
  }

  root.RoutinaEngine = {
    QUESTIONS: QUESTIONS,
    plannedQuestions: plannedQuestions,
    canResolve: canResolve,
    derive: derive,
    plan: plan,
    verdictKey: verdictKey,
    coverage: coverage,
    unstockedTracks: unstockedTracks,
    applySignals: applySignals,
    _internals: {
      pick: pick, eligibility: eligibility, slotPlan: slotPlan,
      rules: rules, helpers: helpers, applyOrder: applyOrder,
      focusWeights: focusWeights
    }
  };
})(typeof window !== 'undefined' ? window : this);
